import { useEffect, useState } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';

import { Button } from '@/component-library/primitives/Button/Button';
import AddPlusCircleIcon from '@/component-library/primitives/Icons/AddPlusCircleIcon/AddPlusCircleIcon';
import ArrowDownUpIcon from '@/component-library/primitives/Icons/ArrowDownUpIcon/ArrowDownUpIcon';
import EditPencilIcon from '@/component-library/primitives/Icons/EditPencilIcon/EditPencilIcon';
import TrashIcon from '@/component-library/primitives/Icons/TrashIcon/TrashIcon';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { APICredential } from '@/models/APICredentials';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';
import { AppRoutes } from '@/utils/routes/router';

export const useAPICredentialsTable = () => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<APICredential>();
  const navigate = useNavigate();

  const isActionAllowed = usePermissionChecker(PermissionsEnum.API_CREDENTIALS, HttpMethodsEnum.PUT);

  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  /** Viewport position for portaled menu (table overflow would clip absolute children). */
  const [dropdownAnchor, setDropdownAnchor] = useState<{ top: number; left: number } | null>(null);

  const handleDropdownClick = (event: React.MouseEvent, rowId: string) => {
    event.stopPropagation();

    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
      setDropdownAnchor(null);
    } else {
      const el = event.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const menuWidth = 160; // w-40
      setDropdownAnchor({
        top: rect.bottom + 4,
        left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      });
      setOpenDropdownId(rowId);
    }
  };

  const handleOnDelete = (id: string | null) => {
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    id && navigate(generatePath(AppRoutes.deleteAPICredentials, { id }));
  };

  useEffect(() => {
    if (!openDropdownId) return;

    const close = () => {
      setOpenDropdownId(null);
      setDropdownAnchor(null);
    };

    document.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openDropdownId]);

  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <div className="flex flex-row items-center gap-1">
          <span className="whitespace-nowrap">{t('APICredentialsPage.nameColumn')}</span>
          <span className="cursor-pointer">
            <ArrowDownUpIcon />
          </span>
        </div>
      ),
      cell: (info) => <span className="block truncate">{info.renderValue()}</span>,
    }),
    columnHelper.accessor('siteId', {
      header: () => <div>{t('APICredentialsPage.siteIdColumn')}</div>,
      cell: (info) => <div>{info.renderValue()}</div>,
    }),
    columnHelper.accessor('apiKey', {
      header: () => <div>{t('APICredentialsPage.APIKeyColumn')}</div>,
      cell: (info) => <div>{info.renderValue()}</div>,
    }),
    columnHelper.accessor('createdAt', {
      header: () => <div>{t('APICredentialsPage.createdAtColumn')}</div>,
      cell: (info) => (
        <div className="flex flex-col">
          <span>
            {new Date(info.renderValue() as string).toLocaleDateString('en-us', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-sm">
            {new Date(info.renderValue() as string).toLocaleTimeString('en-us', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('lastUsed', {
      header: () => <div>{t('APICredentialsPage.lastUsedAtColumn')}</div>,
      cell: (info) => (
        <div className="flex flex-col">
          <span>
            {new Date(info.renderValue() as string).toLocaleDateString('en-us', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-sm">
            {new Date(info.renderValue() as string).toLocaleTimeString('en-us', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => <div></div>,
      cell: (info) => {
        const rowId = info.row.original.id;

        return (
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-end">
              <div
                onClick={(e) => handleDropdownClick(e, rowId)}
                className="cursor-pointer text-[36px] font-normal text-gray-400 flex items-center justify-center h-8 w-8 hover:bg-actionButtonHoverBG hover:rounded-md hover:text-white active:bg-actionButtonHoverBG active:rounded-md active:text-white"
              >
                <span className="pb-5">...</span>
              </div>
            </div>
            {openDropdownId === rowId &&
              dropdownAnchor &&
              createPortal(
                <div
                  className="fixed z-[200] p-2 shadow-md w-40 rounded-lg bg-custom-darkBlue border-[0.063rem] border-gray-800"
                  style={{ top: dropdownAnchor.top, left: dropdownAnchor.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="147" height="2" viewBox="0 0 147 1" fill="none">
                    <path d="M0 1H145.878H147" stroke="#313B4B" />
                  </svg>
                  <div
                    className="group/item flex items-center gap-2 px-2 py-3 mt-1 cursor-pointer rounded-lg hover:bg-deleteButtonHoverBG text-red-400"
                    onClick={() => {
                      handleOnDelete(info.renderValue());
                      setOpenDropdownId(null);
                      setDropdownAnchor(null);
                    }}
                  >
                    <span>
                      <TrashIcon color="#ff9296" />
                    </span>
                    <span className="font-normal">{t('WorkspacesPage.linkDelete')}</span>
                  </div>
                </div>,
                document.body,
              )}
            <ProvideToasts>
              <Toast
                isOpen={open}
                setOpen={setOpen}
                message={toastInfo.message}
                toastType={toastInfo.toastType as ToastType}
              />
            </ProvideToasts>
          </div>
        );
      },
    }),
  ];

  return { columns };
};
